import { IBingoBoard } from '@/interfaces/powerMarkets/bingo/IBingoBoard.ts';
import { IPageData } from '@/interfaces/general/IPageData.ts';
import { IUserSettingsStoreActions } from '@/interfaces/actions/enact/IUserSettingsStoreActions.ts';
import { userSettingsApi } from '@/api/powerMarkets/userSettingsApi.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import {
    IDashboard, DashboardOverrideName, IDashboardOverrides,
} from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { IDeleteItemWithOrderingPayload } from '@/interfaces/powerMarkets/payloads/IDeleteItemWithOrderingPayload.ts';
import { IItemsWithOrderingPayload } from '@/interfaces/powerMarkets/payloads/IItemsWithOrderingPayload.ts';
import { ILeaderboardUserOptions } from '@/interfaces/powerMarkets/settings/pages/ILeaderboardSettings.ts';
import { IMapPageSettingsData } from '@/interfaces/powerMarkets/settings/pages/IMapPageSettings.ts';
import { IMapLivePageSettings } from '@/interfaces/powerMarkets/settings/pages/IMapLivePageSettings.ts';
import { IPageSettingsPayload } from '@/interfaces/powerMarkets/payloads/IPageSettingsPayload.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';
import { publishToast } from '@/utils/Toast.ts';
import { useToast } from 'primevue/usetoast';
import { ApiPromise } from '@/api/useApi.ts';
import { IUserTableFiltersWithName } from '@/interfaces/powerMarkets/payloads/IUserTableFiltersWithName.ts';
import { DEFAULT_SITE_SETTINGS } from '@/constants/powerMarkets/defaultSiteSettings.ts';
import { authState } from '@lcp/http-auth';
import { sanitizeLeaderboardGroupForPersist } from '@/helpers/powerMarkets/benchmarkPoolHelpers.ts';
import { tryCatch } from '@/helpers/tryCatch.ts';
import { LockedAccountEnum } from '@/enums/powerMarkets/LockedAccountEnum.ts';
import { sendOtherTabUserSettingsChange } from '@/realTimeConnection/broadcastChannel.ts';
import {
    applyPlantGroupSettingsWithLegacyMirrors,
} from '@/helpers/powerMarkets/plantGroupLegacySettings.ts';
import type { IPlantGroupSettingsUpdate } from '@/interfaces/powerMarkets/settings/IPlantGroupSettings.ts';
import { IBoaDashboardMapSettings } from '@/interfaces/powerMarkets/settings/pages/IBoaDashboardMapSettings.ts';
import { IExtraChartInfoPayload } from '@/interfaces/powerMarkets/payloads/IExtraChartInfoPayload.ts';
import {
    IChartModule, IModule, isModuleWithInfoProps, ModuleType,
} from '@/interfaces/powerMarkets/IModule.ts';
import { applyDashboardDateOverrideMirrors } from '@/helpers/powerMarkets/dashboardDateOverrideLegacySettings.ts';
import {
    IPlantActivitySettings, IUserSettings,
} from '@/interfaces/powerMarkets/settings/IUserSettings.ts';
import { IRemitSettings } from '@/interfaces/powerMarkets/settings/pages/IRemitSettings.ts';
import { cloneDeep } from '@/utils/global.ts';

import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { DEFAULT_USER_SETTINGS } from '@/constants/powerMarkets/defaultUserSettings.ts';
import { mergeIgnoringNulls } from '@/helpers/mergeObjectsIgnoringNulls.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import { IIndexGroup } from '@/interfaces/benchmarks/index/IIndexGroup.ts';
import { IGbIndexLegacyPageSettings } from '@/interfaces/benchmarks/index/IIndexLegacySettings.ts';
import { ILeaderboardGroup } from '@/interfaces/powerMarkets/leaderboard/ILeaderboardGroup.ts';
import { DEFAULT_LEADERBOARD_ICON, DEFAULT_LEADERBOARD_ICON_COLOUR } from '@/constants/powerMarkets/leaderboardPresets.ts';
import { TReorderableSettingsType } from '@/types/powerMarkets/TOrderedSettingsType.ts';
import { normalizeSubscriptionsWithOrder } from '@/utils/notifications/normalizeSubscriptionSettings.ts';

const useUserSettingsActions = (): IUserSettingsStoreActions => {
    const userSettingsStore = useUserSettingsStore();
    const userAccessStore = useUserAccessStore();
    const dataStore = useDataStore();
    const toast = useToast();

    async function deleteCompanyDashboard (id: string): Promise<void> {
        userSettingsStore.deleteCompanyDashboard(id);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.deleteCompanyDashboard(id);
    }

    async function deleteItemWithOrdering (payload: IDeleteItemWithOrderingPayload): Promise<void> {
        userSettingsStore.deleteItemWithOrdering(payload.id, payload.type, payload.ordering);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.deleteItemWithOrdering(payload);
    }

    async function deleteSubscription (id: string): Promise<void> {
        const ordering = userSettingsStore.userSettings.subscriptionsWithOrder.order.data.filter(orderId => orderId !== id);
        await deleteItemWithOrdering({
            id,
            ordering,
            type: 'notification',
        });
    }

    function reorderItemsWithOrdering (
        type: TReorderableSettingsType,
        ordering: Array<string>,
        setOrdering: (ordering: Array<string>) => void,
    ): void {
        const payload: IItemsWithOrderingPayload<null> = {
            id: '',
            type,
            data: null,
            ordering,
        };

        setOrdering(ordering);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postItemWithOrdering(payload);
    }

    async function fetchCompanyDashboards (): Promise<void> {
        return userSettingsApi.getCompanyDashboards().then(res => {
            userSettingsStore.setCompanyDashboards(res.data);
        });
    }

    async function fetchCompanyUsers (): Promise<boolean> {
        return userSettingsApi.getCompanyUsers().then(res => {
            userSettingsStore.setCompanyUsers(res.data);
            return true;
        });
    }

    // Some historic user settings have dashboard records and their ordering out of sync for an unknown reason.
    // Repair the order on load so valid dashboards remain accessible and stale IDs cannot break later saves.
    function normaliseDashboardOrder (): void {
        const dashboards = userSettingsStore.userSettings.dashboardsWithOrder;
        const seenDashboardIds = new Set<string>();
        const normalisedOrder = dashboards.order.data.filter(dashboardId => {
            if (!dashboards.objectsToStore[dashboardId] || seenDashboardIds.has(dashboardId)) return false;
            seenDashboardIds.add(dashboardId);
            return true;
        });

        Object.keys(dashboards.objectsToStore).forEach(dashboardId => {
            if (seenDashboardIds.has(dashboardId)) return;
            seenDashboardIds.add(dashboardId);
            normalisedOrder.push(dashboardId);
        });

        const orderChanged = normalisedOrder.length !== dashboards.order.data.length
            || normalisedOrder.some((dashboardId, index) => dashboardId !== dashboards.order.data[index]);
        if (orderChanged) reorderDashboards(normalisedOrder);
    }

    function setUserSettingsOverrides (userSettings: IUserSettings): boolean {
        // TEMP dual-run: normalizeSubscriptionsWithOrder (nbd→ndb + null limiters).
        // Delete once old site is gone — see normalizeSubscriptionSettings.ts.
        const normalisedUserSettings: IUserSettings = {
            ...userSettings,
            subscriptionsWithOrder: normalizeSubscriptionsWithOrder(userSettings.subscriptionsWithOrder),
        };
        userSettingsStore.setUserSettings(normalisedUserSettings);
        normaliseDashboardOrder();
        if (normalisedUserSettings.pageData.map) userSettingsStore.setMapSettings(normalisedUserSettings.pageData.map.data);
        if (normalisedUserSettings.activeTab?.data) userSettingsStore.updateActiveDashboard(normalisedUserSettings.activeTab.data);

        return true;
    }

    async function postNewUserSettings (): Promise<boolean> {
        const newSiteSettings = { ...DEFAULT_SITE_SETTINGS };
        newSiteSettings.userId = crypto.randomUUID();
        newSiteSettings.userEmail = authState.value.userInfo!.username;

        const { error: userSettingCreatedError } = await tryCatch(userSettingsApi.postCreateNewUser(newSiteSettings));
        if (userSettingCreatedError) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'There was an error creating user settings', life: 3000,
            });
            return false;
        }
        return true;
    }

    function runLeaderboardMigrationIfNeeded (): void {
        const order = userSettingsStore.userSettings.leaderboardsWithOrder.order.data;
        const bmData = userSettingsStore.userSettings.pageData.bmLeaderboard.data;
        if (order.length > 0) return;

        const defaultGroup: ILeaderboardGroup = {
            id: crypto.randomUUID(),
            name: 'My Leaderboard',
            icon: DEFAULT_LEADERBOARD_ICON,
            iconColour: DEFAULT_LEADERBOARD_ICON_COLOUR,
            description: 'Your existing leaderboard settings',
            userOptions: cloneDeep(bmData.userOptions),
        };
        createLeaderboardGroup(defaultGroup);
        updateLeaderboardGroupTab(defaultGroup.id);
    }

    async function fetchUserSettings (): Promise<void> {
        if (!authState.value.loggedIn) return;
        if (!userAccessStore.userAccess.enact.shortTermMarkets) return; // For now, no point requesting old enact user settings if they don't have short term markets
        const { data: userSettings, error } = await tryCatch(userSettingsApi.getUserSettings());
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'There was an error fetching user settings', life: 3000,
            });
            return;
        }

        if (userSettings) {
            const userSettingsIgnoreNulls = mergeIgnoringNulls(DEFAULT_USER_SETTINGS, userSettings);
            const successful = setUserSettingsOverrides(userSettingsIgnoreNulls);
            runLeaderboardMigrationIfNeeded();
            if (successful) return;
        }

        const createdUserSettings = await postNewUserSettings();
        if (!createdUserSettings) return;

        const { data: newCreatedUserSettings } = await tryCatch(userSettingsApi.getUserSettings());

        if (newCreatedUserSettings) {
            const successful = setUserSettingsOverrides(newCreatedUserSettings);
            runLeaderboardMigrationIfNeeded();
            // eslint-disable-next-line no-useless-return
            if (successful) return;
        }

        // Note: at the moment, while enact on spark is not live, we are pointing at user setting staging which does not have permission to
        // create new user settings. We will need to uncomment this when we go live and point at user settings prod
        // toast.add({
        //     severity: 'error', summary: 'Error', detail: 'There was an error fetching user settings', life: 3000,
        // });
    }

    function reorderDashboards (ordering: Array<string>): void {
        reorderItemsWithOrdering('dashboard', ordering, userSettingsStore.setDashboardOrdering);
    }

    function updateExtraChartOptions (data: IExtraChartInfoPayload): void {
        const dashboardId = userSettingsStore.userSettings.dashboardsWithOrder.order.data[data.viewKey];
        const dashboard = userSettingsStore.userSettings.dashboardsWithOrder.objectsToStore[dashboardId]?.data;
        if (!dashboard?.modules) return;

        const module = dashboard.modules[data.moduleKey] as IChartModule;
        if (!module) return;

        if (!module.extraChartInfo) {
            module.extraChartInfo = {};
        }

        try {
            (module.extraChartInfo as Record<string, unknown>)[data.property] = data.value; // We can't type this any more strictly due to the dynamic nature of the extraChartInfo object
        } catch (error) {
            console.error('Failed to update extraChartInfo:', error);
        }
    }

    function replaceNonEuCountry (): void {
        const userCountryName = userSettingsStore.userSettings.otherSettings.data.country;
        if (userCountryName === 'None') return; // Will be true in case where user has chosen to have no default country
        const currentUserCountry = dataStore.staticStore.countries.find(countryItem => countryItem.id === userCountryName || countryItem.mainSubCountryId === userCountryName);
        if (currentUserCountry?.hasEuropeAccess) return;
        const countryWithEuropeAccess = dataStore.staticStore.countries.find(countryItem => countryItem.hasEuropeAccess);
        if (countryWithEuropeAccess === undefined) return;
        userSettingsStore.updateUserCountry(countryWithEuropeAccess.id);
    }

    async function updateCompanyDashboards (dashboards: Array<string>, uniqueIds: Array<string>): Promise<void> {
        return userSettingsApi.postShareDashboardsWithCompany({ dashboards, uniqueIds }).then(async () => fetchCompanyDashboards());
    }

    function updateDashboardTab (data: IDashboard | null): void {
        userSettingsStore.updateActiveDashboard(data);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postActiveTab({ data: data ?? '', type: 'dashboard' });
    }

    function updateBingoTab (tabId: string): void {
        userSettingsStore.updateActiveBingoId(tabId);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postActiveTab({ data: tabId, type: 'bingo' });
    }

    function updateIndexGroupTab (tabId: string): void {
        userSettingsStore.updateActiveIndexGroupId(tabId);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postActiveTab({ data: tabId, type: 'indexGroup' });
    }

    function updateUserTableFilters (payload: IUserTableFiltersWithName): void {
        userSettingsStore.setUserTableFilters(payload);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postUserTableFilters(payload).catch(() => {
            publishToast(toast, 'There was an error saving the table filters', 'error');
        });
    }

    function deleteLiveTableFiltersForModules (modules: Array<IModule>): void {
        modules.forEach((module) => {
            if (isModuleWithInfoProps(module) && module.info.type === ModuleType.LiveTable) {
                updateUserTableFilters({ name: `liveTable_${module.info.id}`, filters: null });
            }
        });
    }

    function createIndexGroup (group: IIndexGroup): void {
        const indexGroupIdOrder = userSettingsStore.userSettings.indexGroupsWithOrder.order.data;

        const payload: IItemsWithOrderingPayload<IIndexGroup> = {
            id: group.id,
            type: 'indexGroup',
            data: group,
            ordering: [group.id, ...indexGroupIdOrder],
        };

        updateIndexGroup(payload);
    }

    function updateIndexGroup (payload: IItemsWithOrderingPayload<IIndexGroup>): void {
        const item: IPageData<IIndexGroup> = {
            id: payload.id,
            data: payload.data,
        };

        userSettingsStore.setIndexGroupWithOrdering(item, payload.ordering);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postItemWithOrdering(payload);
    }

    function reorderIndexGroups (newOrder: Array<string>): void {
        reorderItemsWithOrdering('indexGroup', newOrder, userSettingsStore.setIndexGroupOrdering);
    }

    async function deleteIndexGroup (id: string): Promise<void> {
        const ordering = userSettingsStore.userSettings.indexGroupsWithOrder.order.data.filter(orderId => orderId !== id);
        await deleteItemWithOrdering({
            id,
            ordering,
            type: 'indexGroup',
        });
    }

    function createLeaderboardGroup (group: ILeaderboardGroup): void {
        const leaderboardGroupIdOrder = userSettingsStore.userSettings.leaderboardsWithOrder.order.data;

        const payload: IItemsWithOrderingPayload<ILeaderboardGroup> = {
            id: group.id,
            type: 'leaderboard',
            data: group,
            ordering: [group.id, ...leaderboardGroupIdOrder],
        };

        updateLeaderboardGroup(payload);
    }

    function updateLeaderboardGroup (payload: IItemsWithOrderingPayload<ILeaderboardGroup>): void {
        const sanitizedPayload = { ...payload, data: sanitizeLeaderboardGroupForPersist(payload.data) };
        const item: IPageData<ILeaderboardGroup> = {
            id: payload.id,
            data: sanitizedPayload.data,
        };

        userSettingsStore.setLeaderboardGroupWithOrdering(item, payload.ordering);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postItemWithOrdering(sanitizedPayload);
    }

    function reorderLeaderboardGroups (newOrder: Array<string>): void {
        reorderItemsWithOrdering('leaderboard', newOrder, userSettingsStore.setLeaderboardGroupOrdering);
    }

    async function deleteLeaderboardGroup (id: string): Promise<void> {
        const ordering = userSettingsStore.userSettings.leaderboardsWithOrder.order.data.filter(orderId => orderId !== id);
        await deleteItemWithOrdering({
            id,
            ordering,
            type: 'leaderboard',
        });
        updateUserTableFilters({ name: `BmLeaderboard_${id}`, filters: null });
    }

    function updateLeaderboardGroupTab (tabId: string | null): void {
        userSettingsStore.updateActiveLeaderboardId(tabId);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        if (tabId) userSettingsApi.postActiveTab({ data: tabId, type: 'leaderboard' });
    }

    function createDashboard (dashboard: IDashboard): void {
        const dashboardIdOrder = userSettingsStore.userSettings.dashboardsWithOrder.order.data;

        const payload: IItemsWithOrderingPayload<IDashboard> = {
            id: dashboard.id,
            type: 'dashboard',
            data: dashboard,
            ordering: [dashboard.id, ...dashboardIdOrder],
        };

        updateDashboard(payload);
    }

    async function updateDashboard (payload: IItemsWithOrderingPayload<IDashboard>): Promise<void> {
        applyDashboardDateOverrideMirrors(payload.data);

        const item: IPageData<IDashboard> = {
            id: payload.id,
            data: payload.data,
        };

        userSettingsStore.setDashboardItemsWithOrdering(item, payload.ordering);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        await userSettingsApi.postItemWithOrdering(payload);
    }

    async function updateWithCurrentDashboard (newValues: { moduleId: string; timezone?: string; periodsPerDay?: number } | null = null): Promise<boolean> {
        if (!userSettingsStore.userSettings.activeTab?.data) return false;

        const settingsData = userSettingsStore.userSettings.otherSettings.data;

        if (settingsData.lockedAccount === LockedAccountEnum.UploadLocked) return false;

        const dashboard = userSettingsStore.userSettings.dashboardsWithOrder.objectsToStore[userSettingsStore.userSettings.activeTab.data.id];

        if (newValues) {
            const module = (dashboard.data.modules as Array<IChartModule>).find(mod => mod.id === newValues.moduleId) ?? null;

            if (module && typeof module.date === 'object') {
                const moduleDate = module.date as IDateObject;
                moduleDate.timezone = newValues.timezone ?? settingsData.timezone;
                moduleDate.periodsPerDay = newValues.periodsPerDay ?? settingsData.periodsInDayForCharts ?? null;
            }
        }

        applyDashboardDateOverrideMirrors(dashboard.data);

        const payload: IItemsWithOrderingPayload<IDashboard> = {
            data: dashboard.data,
            id: dashboard.id,
            ordering: null,
            type: 'dashboard',
        };

        return userSettingsApi.postItemWithOrdering(payload).then(() => true).catch(() => {
            publishToast(toast, 'There was an error saving the dashboard', 'error');
            return false;
        });
    }

    async function updateDashboards (dashboardIds?: Array<string>, dashboardViewKey?: number, showToast?: boolean): Promise<void> {
        const promises: Array<ApiPromise<void>> = [];

        let dashboardIdsToUse = dashboardIds;

        if (dashboardViewKey !== undefined && dashboardViewKey !== null) {
            const dashboardId = userSettingsStore.userSettings.dashboardsWithOrder.order.data[dashboardViewKey];
            if (dashboardId && userSettingsStore.userSettings.dashboardsWithOrder.objectsToStore[dashboardId]) {
                dashboardIdsToUse = [dashboardId];
            }
        }

        if (dashboardIdsToUse) {
            dashboardIdsToUse.forEach(dashboardId => {
                if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return false;
                const dashboard = userSettingsStore.userSettings.dashboardsWithOrder.objectsToStore[dashboardId];

                applyDashboardDateOverrideMirrors(dashboard.data);

                const payload: IItemsWithOrderingPayload<IDashboard> = {
                    data: dashboard.data,
                    id: dashboard.id,
                    ordering: null,
                    type: 'dashboard',
                };
                promises.push(userSettingsApi.postItemWithOrdering(payload));
                return null;
            });
        }

        const final = await Promise.allSettled(promises).then((resArray) => {
            if (!showToast) return;
            if (resArray.every(res => res.status === 'fulfilled')) {
                publishToast(toast, 'Dashboards saved successfully', 'success');
            } else if (resArray.every(res => res.status === 'rejected')) {
                publishToast(toast, 'There was an error saving the dashboards', 'error');
            } else if (resArray.some(res => res.status === 'rejected')) {
                publishToast(toast, 'There was an error saving some of the dashboards', 'error');
            }
        });
        return final;
    }

    function updateBingoBoard (payload: IItemsWithOrderingPayload<IBingoBoard>): void {
        const item: IPageData<IBingoBoard> = {
            id: payload.id,
            data: payload.data,
        };

        userSettingsStore.setBingoItemsWithOrdering(item, payload.ordering);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postItemWithOrdering(payload);
    }

    async function updatePageDatesSettingsSingle (prop: DashboardOverrideName, newSetting: IDashboardOverrides): Promise<void> {
        await userSettingsStore.setPageDatesSettingSingle(prop, newSetting);
    }

    async function updateGbIndexSettings (options: IGbIndexLegacyPageSettings): Promise<void> {
        userSettingsStore.setGbIndexSettings(options);
        if (!authState.value.loggedIn) return;
        if (!userAccessStore.userAccess.enact.shortTermMarkets) return;
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        await userSettingsApi.postPageSettings({ pageName: 'plantIndex', data: options });
    }

    async function updateLeaderboardSettings (payload: ILeaderboardUserOptions): Promise<void> {
        userSettingsStore.setLeaderboardSettings(payload);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        await userSettingsApi.postPageSettings({ pageName: 'bmLeaderboard', data: { userOptions: payload } });
    }

    async function updateLeaderboardSettingSingle<K extends keyof ILeaderboardUserOptions> (prop: K, newSetting: ILeaderboardUserOptions[K], postUpdate?: boolean): Promise<void> {
        await userSettingsStore.setLeaderboardSettingSingle(prop, newSetting, postUpdate);
    }

    async function updateLeaderboardSettingMultiple<K extends keyof ILeaderboardUserOptions> (settings: Array<[K, ILeaderboardUserOptions[K]]>, postUpdate?: boolean): Promise<void> {
        await userSettingsStore.setLeaderboardSettingMultiple(settings, postUpdate);
    }

    function updateMapSettings (pageSettingsUpdate: IPageSettingsPayload<IMapPageSettingsData>): void {
        userSettingsStore.setMapSettings(pageSettingsUpdate.data);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postPageSettings(pageSettingsUpdate);
    }

    function updateBingoMapSettings (pageSettingsUpdate: IPageSettingsPayload<IMapLivePageSettings>): void {
        userSettingsStore.setBingoMapSettings(pageSettingsUpdate.data);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postPageSettings(pageSettingsUpdate);
    }

    function updateBoaDashboardMapSettings (pageSettingsUpdate: IPageSettingsPayload<IBoaDashboardMapSettings>): void {
        userSettingsStore.setBoaDashboardMapSettings(pageSettingsUpdate.data);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postPageSettings(pageSettingsUpdate);
    }

    function updatePlantActivitySettings (pageSettingsUpdate: IPageSettingsPayload<IPlantActivitySettings>): void {
        userSettingsStore.userSettings.pageData.plantActivity.data = pageSettingsUpdate.data;
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postPageSettings(pageSettingsUpdate);
    }

    function updateRemitSettings (pageSettingsUpdate: IPageSettingsPayload<IRemitSettings>): void {
        userSettingsStore.setRemitSettings(pageSettingsUpdate.data);
        if (userSettingsStore.userSettings.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        userSettingsApi.postPageSettings(pageSettingsUpdate);
    }

    function updateUserSettings<K extends keyof ISiteSettings> (prop: K, newSetting: ISiteSettings[K], postUpdate?: boolean): void {
        userSettingsStore.setSiteSettings(prop, newSetting, postUpdate);
    }

    /**
     * Save the current plant-group settings in both the Data Portal format and the
     * legacy format still read by old Enact. Remove this adapter when old Enact no
     * longer needs the legacy plant-group fields.
     */
    function savePlantGroupSettings (update: IPlantGroupSettingsUpdate): void {
        const otherSettingsData = userSettingsStore.userSettings.otherSettings.data;
        const payload = applyPlantGroupSettingsWithLegacyMirrors(
            otherSettingsData,
            update,
            dataStore.allPlants,
        );

        if (otherSettingsData.lockedAccount === LockedAccountEnum.UploadLocked) return;

        userSettingsApi.postUserSettings(payload).then(() => sendOtherTabUserSettingsChange());
    }

    function updateFallbackCountry (countryId: string): void {
        if (countryId === userSettingsStore.userSettings.otherSettings.data.fallbackCountry) return;

        const countryExists = dataStore.staticStore.countries.find(country => country.id === countryId || country.mainSubCountryId === countryId);
        if (!countryExists) return;
        userSettingsStore.setSiteSettings('fallbackCountry', countryId, true);
    }

    return {
        createDashboard,
        deleteCompanyDashboard,
        deleteItemWithOrdering,
        deleteSubscription,
        fetchCompanyDashboards,
        fetchCompanyUsers,
        fetchUserSettings,
        reorderDashboards,
        updateExtraChartOptions,
        replaceNonEuCountry,
        updateBingoBoard,
        updateBingoTab,
        updateCompanyDashboards,
        createIndexGroup,
        updateIndexGroup,
        reorderIndexGroups,
        deleteIndexGroup,
        updateIndexGroupTab,
        createLeaderboardGroup,
        updateLeaderboardGroup,
        reorderLeaderboardGroups,
        deleteLeaderboardGroup,
        updateLeaderboardGroupTab,
        updateDashboard,
        updateWithCurrentDashboard,
        updateDashboardTab,
        updateUserTableFilters,
        deleteLiveTableFiltersForModules,
        updatePageDatesSettingsSingle,
        updateGbIndexSettings,
        updateLeaderboardSettings,
        updateLeaderboardSettingSingle,
        updateLeaderboardSettingMultiple,
        updateMapSettings,
        updateBingoMapSettings,
        updateBoaDashboardMapSettings,
        updatePlantActivitySettings,
        updateRemitSettings,
        updateUserSettings,
        savePlantGroupSettings,
        updateDashboards,
        updateFallbackCountry,
        runLeaderboardMigrationIfNeeded,
    };
};

export { useUserSettingsActions };
